import { computed, toValue } from 'vue';
import { CatalogPromotions } from 'ihunter-common';
import { useCatalog } from './useCatalog';
import { usePurchases } from './usePurchases';
import { useCart } from './useCart';
import { SubscriptionSku } from './useConstants';

export function useStoreProduct(sku) {
    const skuRef = computed(() => toValue(sku));

    const { catalog, products, prices } = useCatalog();
    const { purchasedProducts } = usePurchases();
    const { cart } = useCart();

    const product = computed(() => products?.value?.[skuRef.value]);
    const purchase = computed(() => purchasedProducts?.value?.[skuRef.value]);

    const productRegion = computed(() => product.value?.region);

    const regionCatalog = computed(() => catalog?.value?.[productRegion.value]);

    const catalogMap = computed(() => {
        if (!regionCatalog.value || !product.value?.resource || !product.value?.layer || !product.value?.map) {
            return;
        }

        return regionCatalog.value?.resources?.[product.value.resource]?.layers?.[product.value.layer]?.maps?.[product.value.map];
    });

    const catalogSubscription = computed(() => {
        if (!regionCatalog.value || !product.value?.subscription) {
            return;
        }

        return regionCatalog.value?.subscriptions?.[product.value.subscription];
    });

    const isProSubscription = computed(() => {
        if (skuRef.value.endsWith(SubscriptionSku.Pro)) {
            return true;
        } else if (skuRef.value.endsWith(SubscriptionSku.Core)) {
            return false;
        }
        return false;
    });

    const catalogItem = computed(() => {
        if (catalogMap.value) {
            return catalogMap.value;
        }
        if (catalogSubscription.value) {
            return catalogSubscription.value;
        }
        return null;
    });

    const productName = computed(() => catalogItem.value?.name ?? (skuRef.value && skuRef.value.replace(/_/g, ' ')) ?? null);

    const update = computed(() => {
        if (!catalogMap.value || !catalogMap.value.products) {
            return;
        }

        const revisions = catalogMap.value.products;

        let latestOwnedVersion = null;

        // Identify latest version already owned
        for (const revision in revisions) {
            if (!purchasedProducts?.value[revision]) {
                continue;
            }

            const ownedVersion = revisions[revision].version;
            if (!latestOwnedVersion || ownedVersion > revisions[latestOwnedVersion].version) {
                latestOwnedVersion = revision;
            }
        }

        // Identify newest available update
        for (const revision in revisions) {
            if (purchasedProducts?.value[revision] || (latestOwnedVersion && revisions[revision].version <= revisions[latestOwnedVersion].version)) {
                continue;
            }

            if (revisions[revision].version > product.value.version) {
                return revision;
            }
        }

        return null;
    });

    const outdated = computed(() => {
        if (!catalogMap.value || !catalogMap.value.products) {
            return;
        }

        // Search for outdated owned map only if this product is latest and unpurchased
        if (!update.value && !purchase.value) {
            for (const purchasedSku in purchasedProducts?.value) {
                const purchasedProduct = products?.value[purchasedSku];
                if (!purchasedProduct) {
                    continue;
                }

                if (
                    purchasedProduct.map === product.value.map &&
                    purchasedProduct.region === product.value.region &&
                    purchasedProduct.version < product.value.version
                ) {
                    return purchasedSku;
                }
            }
        }
        return null;
    });

    const productPrice = computed(() => {
        if (outdated.value) {
            return CatalogPromotions.ApplyUpgradeDiscount(prices?.value?.[skuRef.value]);
        }
        return prices?.value?.[skuRef.value];
    });

    const originalPrice = computed(() => prices?.value?.[skuRef.value]);

    const canPurchase = computed(() => {
        if (!product.value) {
            return false;
        }

        if (!!purchase.value && !update.value) {
            return false;
        }

        // Prevent purchase if map is unavailable in catalog, even if not owned
        if (catalogMap.value && catalogMap.value?.unavailable) {
            return false;
        }

        return true;
    });

    return {
        canPurchase,

        productRegion,
        productName,
        productPrice,
        originalPrice,

        catalogItem,
        isProSubscription,

        isMap: computed(() => !!catalogMap.value),
        isSubscription: computed(() => !!catalogSubscription.value),
        isOwned: computed(() => !!purchase.value),
        isUpdate: computed(() => !!outdated.value),

        ownedVersion: computed(() => outdated.value?.version),
        productVersion: computed(() => product.value?.version),

        hasUpdate: computed(() => !!update.value),
        isUpgrade: computed(() => !!outdated.value),

        hasDiscount: computed(() => productPrice.value !== originalPrice.value),

        inCart: computed(() => !!cart.value?.[skuRef.value]),
    };
}
